import { normalizeMaterialKey } from "./manualPriceProvider";

export const PRICE_SOURCE_CRAFTING = "crafting";
export const PRICE_SOURCE_FREE = "free";

const WOOD_TYPES = ["oak", "spruce", "birch", "jungle", "acacia", "dark_oak", "mangrove", "cherry", "pale_oak"];
const STEM_TYPES = ["crimson", "warped"];
const WOOL_COLORS = [
  "white",
  "orange",
  "magenta",
  "light_blue",
  "yellow",
  "lime",
  "pink",
  "gray",
  "light_gray",
  "cyan",
  "purple",
  "blue",
  "brown",
  "green",
  "red",
  "black",
];

const FREE_MATERIALS = new Set(["water", "water_bucket"]);

const getDirectPrice = (material, providers) => {
  for (const provider of providers) {
    const price = provider.getPrice(material);
    if (price !== null) return { price, source: provider.source };
  }
  return null;
};

const getCraftingRecipe = (material) => {
  const normalizedMaterial = normalizeMaterialKey(material);
  if (FREE_MATERIALS.has(normalizedMaterial)) {
    return { free: true };
  }

  for (const woodType of WOOD_TYPES) {
    if (normalizedMaterial === `${woodType}_wood`) return { input: `${woodType}_log`, factor: 4 / 3 };
    if (normalizedMaterial === `stripped_${woodType}_wood`) return { input: `stripped_${woodType}_log`, factor: 4 / 3 };
    if (normalizedMaterial === `${woodType}_planks`) return { input: `${woodType}_log`, factor: 1 / 4 };
    if (normalizedMaterial === `${woodType}_slab`) return { input: `${woodType}_planks`, factor: 1 / 2 };
    if (normalizedMaterial === `${woodType}_pressure_plate`) return { input: `${woodType}_planks`, factor: 2 };
  }

  for (const stemType of STEM_TYPES) {
    if (normalizedMaterial === `${stemType}_hyphae`) return { input: `${stemType}_stem`, factor: 4 / 3 };
    if (normalizedMaterial === `stripped_${stemType}_hyphae`) return { input: `stripped_${stemType}_stem`, factor: 4 / 3 };
    if (normalizedMaterial === `${stemType}_planks`) return { input: `${stemType}_stem`, factor: 1 / 4 };
    if (normalizedMaterial === `${stemType}_slab`) return { input: `${stemType}_planks`, factor: 1 / 2 };
    if (normalizedMaterial === `${stemType}_pressure_plate`) return { input: `${stemType}_planks`, factor: 2 };
  }

  for (const woolColor of WOOL_COLORS) {
    if (normalizedMaterial === `${woolColor}_carpet`) return { input: `${woolColor}_wool`, factor: 2 / 3 };
  }

  const directRecipes = {
    stone_slab: { input: "stone", factor: 1 / 2 },
    smooth_stone_slab: { input: "smooth_stone", factor: 1 / 2 },
    cobblestone_slab: { input: "cobblestone", factor: 1 / 2 },
    mossy_cobblestone_slab: { input: "mossy_cobblestone", factor: 1 / 2 },
    stone_brick_slab: { input: "stone_bricks", factor: 1 / 2 },
    sandstone_slab: { input: "sandstone", factor: 1 / 2 },
    end_stone_brick_slab: { input: "end_stone_bricks", factor: 1 / 2 },
    granite_slab: { input: "granite", factor: 1 / 2 },
    andesite_slab: { input: "andesite", factor: 1 / 2 },
    deepslate_slab: { input: "deepslate", factor: 1 / 2 },
    cobbled_deepslate_slab: { input: "cobbled_deepslate", factor: 1 / 2 },
    mud_brick_slab: { input: "mud_bricks", factor: 1 / 2 },
    cut_copper_slab: { input: "cut_copper", factor: 1 / 2 },
    waxed_cut_copper_slab: { input: "waxed_cut_copper", factor: 1 / 2 },
    waxed_exposed_cut_copper_slab: { input: "waxed_exposed_cut_copper", factor: 1 / 2 },
    waxed_weathered_cut_copper_slab: { input: "waxed_weathered_cut_copper", factor: 1 / 2 },
    waxed_oxidized_cut_copper_slab: { input: "waxed_oxidized_cut_copper", factor: 1 / 2 },
    stone_pressure_plate: { input: "stone", factor: 2 },
    light_weighted_pressure_plate: { input: "gold_ingot", factor: 2 },
    heavy_weighted_pressure_plate: { input: "iron_ingot", factor: 2 },
  };

  return directRecipes[normalizedMaterial] || null;
};

export const resolveCraftingPrice = (material, providers, visitedMaterials = new Set()) => {
  const normalizedMaterial = normalizeMaterialKey(material);
  if (visitedMaterials.has(normalizedMaterial)) return null;
  visitedMaterials.add(normalizedMaterial);

  const recipe = getCraftingRecipe(normalizedMaterial);
  if (recipe === null) return null;
  if (recipe.free) return { price: 0, source: PRICE_SOURCE_FREE };

  const directBasePrice = getDirectPrice(recipe.input, providers);
  if (directBasePrice !== null) {
    return { price: directBasePrice.price * recipe.factor, source: PRICE_SOURCE_CRAFTING, recipe };
  }

  const craftedBasePrice = resolveCraftingPrice(recipe.input, providers, visitedMaterials);
  if (craftedBasePrice !== null) {
    return { price: craftedBasePrice.price * recipe.factor, source: PRICE_SOURCE_CRAFTING, recipe };
  }

  return null;
};
